import React, { useState } from 'react';
import styles from './NewOrganization.module.scss';
import cn from 'classnames';
import { Form, Icon, Modal, Select } from 'antd';
import { ReactComponent as OrganizationIcon } from '../../../../resource/OrganizationIcon.svg';
import DispexInput from '../../../../components/DispexInput';
import RoundedButton from '../../../../components/RoundedButton/RoundedButton';
import useOrgsByInn from '../../mutation/useOrgsByInn';
import useDebounceCallback from '../../hooks/useDebounceCallback';
import useCreateOrgDocs from '../../mutation/useCreateOrgDocs';

const Footer = ({ onCancel, onSubmit, isLoadingOrgDocs }) => {
  return (
    <div className={styles.footer}>
      <RoundedButton
        text="Отменить"
        type="primary"
        onClick={onCancel}
        className={cn(styles.btn, styles.btnCancel)}
      />
      <RoundedButton
        text="Добавить"
        type="primary"
        onClick={onSubmit}
        className={cn(styles.btn, styles.btnSave)}
        iconRight={isLoadingOrgDocs && <Icon type="loading" />}
      />
    </div>
  );
};

const NewOrganizationNode = ({ form, visible, onCancel, serviceCompanyId }) => {
  const { getFieldDecorator, validateFields, setFieldsValue, resetFields } =
    form;

  const [search, setSearch] = useState('');
  const [orgs, setOrgs] = useState([]);

  const { isLoading, getOrgsByInn } = useOrgsByInn();

  const { createOrgDocs, isLoading: isLoadingOrgDocs } = useCreateOrgDocs();

  const debouncedSearch = useDebounceCallback(async (value) => {
    setSearch(value);

    const result = await getOrgsByInn(value);

    if (result !== 'Error') {
      setOrgs(result);
    }
  }, 200);

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (!err) {
        await createOrgDocs({ ...values, serviceCompanyId });

        onCancel();

        resetFields();
      }
    });
  };

  const options = orgs?.map((org, index) => {
    return (
      <Select.Option
        key={org.inn}
        value={`${org.inn} ${index}`}
      >
        {org.inn}
        {org.inn && org.name && ' '}
        {org.name}
        {org.name && org.address && ' '}
        {org.address && `( ${org.address} )`}
      </Select.Option>
    );
  });

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <OrganizationIcon />
          Добавить организацию
        </div>
      }
      destroyOnClose
      footer={
        <Footer
          onCancel={onCancel}
          onSubmit={handleSubmit}
          isLoadingOrgDocs={isLoadingOrgDocs}
        />
      }
      visible={visible}
      onCancel={onCancel}
      className={styles.modalCustom}
      width={600}
      zIndex={9999999}
    >
      <Form>
        <div className={styles.container}>
          <div className={styles.row}>
            <Form.Item
              label="Поиск организации по названию, ИНН"
              validateStatus={search.length >= 300 ? 'warning' : ''}
              help={
                search.length >= 300
                  ? 'Вы достигли максимума в 300 символов'
                  : ''
              }
            >
              <Select
                getPopupContainer={(trigger) => trigger.parentNode}
                showSearch
                placeholder="Введите название организации, ИНН"
                onSearch={(search) => {
                  if (search.length && search.length <= 300) {
                    debouncedSearch(search);
                  }
                }}
                showArrow={false}
                filterOption={false}
                onChange={(key) => {
                  const index = key.split(' ').pop();
                  const selectedOrg = orgs[index];
                  setFieldsValue({
                    inn: selectedOrg.inn,
                    name: selectedOrg.name,
                    directorFio: selectedOrg.director,
                  });
                }}
                defaultActiveFirstOption={false}
                loading={isLoading}
                notFoundContent={
                  search && !isLoading && 'Не найдены подходящие компании'
                }
                className={styles.customSelect}
              >
                {options}
              </Select>
            </Form.Item>
            <div style={{ display: 'flex', gap: 10 }}>
              <Form.Item
                label="ИНН"
                style={{ margin: 0, flex: 1 }}
              >
                {getFieldDecorator('inn', {
                  initialValue: '',
                  rules: [
                    {
                      required: false,
                      min: 10,
                      max: 12,
                      message: 'Пожалуйста, укажите коректный ИНН',
                    },
                  ],
                })(
                  <DispexInput
                    style={{ height: '40px' }}
                    placeholder="Номер ИНН"
                  />
                )}
              </Form.Item>
              <Form.Item
                label="Наименование"
                style={{ margin: 0, flex: 1 }}
              >
                {getFieldDecorator('name', {
                  initialValue: '',
                  rules: [
                    {
                      required: true,
                      min: 3,
                      message: 'Пожалуйста, укажите наименование организации',
                    },
                  ],
                })(
                  <DispexInput
                    style={{ height: '40px' }}
                    placeholder="Наименование"
                  />
                )}
              </Form.Item>
            </div>
          </div>
          <div className={styles.row}>
            <Form.Item
              label="Email"
              style={{ margin: 0 }}
            >
              {getFieldDecorator('email', {
                initialValue: '',
                rules: [
                  {
                    type: 'email',
                    message: 'Введите корректный email',
                  },
                ],
              })(
                <DispexInput
                  style={{ height: '40px' }}
                  placeholder="Адрес электронной почты"
                />
              )}
            </Form.Item>
            <Form.Item
              label="ФИО руководителя"
              style={{ margin: 0 }}
            >
              {getFieldDecorator('director', {
                initialValue: '',
              })(
                <DispexInput
                  style={{ height: '40px' }}
                  placeholder="Введите ФИО"
                />
              )}
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

const NewOrganization = Form.create('new-organization')(NewOrganizationNode);
export default NewOrganization;
